// Enhanced connectivity testing features
// Separate module to keep main component manageable

// DNS Resolution Testing
export interface DNSTest {
  resolver: string;
  hostname: string;
  time: number;
  ip?: string;
  error?: string;
}

export async function testDNSResolution(hostname: string): Promise<DNSTest[]> {
  // Extract hostname from URL
  const url = new URL(hostname.startsWith('http') ? hostname : `https://${hostname}`);
  const host = url.hostname;

  const resolvers = [
    { name: 'Cloudflare', endpoint: 'https://cloudflare-dns.com/dns-query', type: 'doh' as const },
    { name: 'Google', endpoint: 'https://dns.google/resolve', type: 'json' as const }
    // Note: Quad9 removed - doesn't support CORS from browsers
  ];

  const results: DNSTest[] = [];

  for (const resolver of resolvers) {
    const start = performance.now();

    try {
      if (resolver.type === 'json') {
        // Google uses JSON API
        const response = await fetch(`${resolver.endpoint}?name=${host}&type=A`, {
          signal: AbortSignal.timeout(5000) // 5s timeout
        });
        const data = await response.json();
        const time = performance.now() - start;

        results.push({
          resolver: resolver.name,
          hostname: host,
          time,
          ip: data.Answer?.[0]?.data || undefined
        });
      } else {
        // DoH with wireformat - use base64url encoding (no padding)
        const dnsQuery = buildDNSQuery(host);
        const dnsQueryB64 = bufferToBase64Url(dnsQuery);

        // Try GET request with base64url-encoded query (RFC 8484)
        const response = await fetch(`${resolver.endpoint}?dns=${dnsQueryB64}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/dns-message'
          },
          signal: AbortSignal.timeout(5000) // 5s timeout
        });

        const time = performance.now() - start;

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.arrayBuffer();
        const ip = parseDNSResponse(data);

        results.push({
          resolver: resolver.name,
          hostname: host,
          time,
          ip
        });
      }
    } catch (error: any) {
      results.push({
        resolver: resolver.name,
        hostname: host,
        time: performance.now() - start,
        error: error.message
      });
    }
  }

  return results;
}

// Simple DNS query builder (A record)
function buildDNSQuery(hostname: string): Uint8Array {
  // Simplified DNS query packet for A record
  // This is a basic implementation - full DNS protocol is complex
  const parts = hostname.split('.');
  const buffer = new Uint8Array(512);
  let offset = 0;

  // Header
  buffer[offset++] = 0x00; buffer[offset++] = 0x00; // ID
  buffer[offset++] = 0x01; buffer[offset++] = 0x00; // Flags: standard query
  buffer[offset++] = 0x00; buffer[offset++] = 0x01; // Questions: 1
  buffer[offset++] = 0x00; buffer[offset++] = 0x00; // Answer RRs
  buffer[offset++] = 0x00; buffer[offset++] = 0x00; // Authority RRs
  buffer[offset++] = 0x00; buffer[offset++] = 0x00; // Additional RRs

  // Question section
  for (const part of parts) {
    buffer[offset++] = part.length;
    for (let i = 0; i < part.length; i++) {
      buffer[offset++] = part.charCodeAt(i);
    }
  }
  buffer[offset++] = 0x00; // End of name

  buffer[offset++] = 0x00; buffer[offset++] = 0x01; // Type: A
  buffer[offset++] = 0x00; buffer[offset++] = 0x01; // Class: IN

  return buffer.slice(0, offset);
}

// Convert Uint8Array to base64url string for URL encoding (RFC 8484)
// base64url uses - and _ instead of + and /, and omits padding =
function bufferToBase64Url(buffer: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < buffer.length; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  const base64 = btoa(binary);
  // Convert to base64url: replace +/= with -_
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, ''); // Remove padding
}

// Simple DNS response parser
function parseDNSResponse(data: ArrayBuffer): string | undefined {
  const view = new DataView(data);

  try {
    // Skip header (12 bytes)
    let offset = 12;

    // Skip question section
    while (offset < view.byteLength) {
      const len = view.getUint8(offset++);
      if (len === 0) break;
      offset += len;
    }
    offset += 4; // Skip type and class

    // Parse answer section
    if (offset + 12 <= view.byteLength) {
      offset += 2; // Skip name pointer
      const type = view.getUint16(offset); offset += 2;
      offset += 6; // Skip class and TTL
      const dataLen = view.getUint16(offset); offset += 2;

      if (type === 1 && dataLen === 4) { // A record
        const ip = `${view.getUint8(offset)}.${view.getUint8(offset + 1)}.${view.getUint8(offset + 2)}.${view.getUint8(offset + 3)}`;
        return ip;
      }
    }
  } catch (e) {
    // Parsing failed
  }

  return undefined;
}

// Protocol Detection
export interface ProtocolInfo {
  protocol: string; // HTTP/1.1, h2, h3
  connectionReused: boolean;
  tlsVersion?: string;
  serverPush?: boolean;
}

export async function detectProtocol(url: string): Promise<ProtocolInfo | null> {
  try {
    // Make request with no-cors mode to avoid CORS issues
    await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors', // Allow request even if CORS fails
      cache: 'no-store',
      signal: AbortSignal.timeout(10000) // 10s timeout
    });

    // Get performance entry
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const entry = entries[entries.length - 1];

    if (!entry) return null;

    const connectionReused = entry.connectEnd - entry.connectStart === 0;

    return {
      protocol: entry.nextHopProtocol || 'unknown',
      connectionReused,
      tlsVersion: entry.secureConnectionStart > 0 ? 'TLS' : undefined,
      serverPush: (entry as any).transferSize === 0 && entry.decodedBodySize > 0
    };
  } catch (error) {
    // CORS or network error - return null
    return null;
  }
}

// NAT and Network Topology Detection
export interface NATInfo {
  localIPs: string[];
  publicIP?: string;
  natType?: string;
  iceCandidates: {
    type: string;
    address: string;
    port: number;
    protocol: string;
  }[];
}

export async function detectNAT(): Promise<NATInfo> {
  return new Promise((resolve) => {
    const config = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };

    const pc = new RTCPeerConnection(config);
    const localIPs = new Set<string>();
    const candidates: any[] = [];

    let timeoutId: NodeJS.Timeout | null = null;

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidate = event.candidate;
        candidates.push({
          type: candidate.type,
          address: candidate.address || 'unknown',
          port: candidate.port || 0,
          protocol: candidate.protocol || 'unknown'
        });

        // Extract local IPs from host candidates
        if (candidate.type === 'host' && candidate.address) {
          localIPs.add(candidate.address);
        }
      } else {
        // ICE gathering complete
        if (timeoutId) clearTimeout(timeoutId); // Clear timeout
        pc.close();

        // Determine NAT type
        let natType = 'Unknown';
        const hasHost = candidates.some(c => c.type === 'host');
        const hasSrflx = candidates.some(c => c.type === 'srflx');
        const hasRelay = candidates.some(c => c.type === 'relay');

        if (hasHost && !hasSrflx && !hasRelay) {
          natType = 'Open Internet (No NAT)';
        } else if (hasSrflx) {
          natType = 'Symmetric NAT';
        } else if (hasRelay) {
          natType = 'Restrictive NAT (TURN required)';
        }

        // Get public IP from srflx candidate
        const publicCandidate = candidates.find(c => c.type === 'srflx');
        const publicIP = publicCandidate?.address;

        resolve({
          localIPs: Array.from(localIPs),
          publicIP,
          natType,
          iceCandidates: candidates
        });
      }
    };

    // Create data channel to trigger ICE
    pc.createDataChannel('test');
    pc.createOffer().then(offer => pc.setLocalDescription(offer));

    // Timeout after 10 seconds
    timeoutId = setTimeout(() => {
      pc.close();
      resolve({
        localIPs: Array.from(localIPs),
        iceCandidates: candidates,
        natType: 'Timeout'
      });
    }, 10000);
  });
}

// Geographic Routing Detection
export interface GeoTest {
  endpoint: string;
  location: string;
  latency: number;
  ip?: string;
}

export async function testGeographicRouting(hostname: string): Promise<GeoTest[]> {
  // Test against known CDN edge locations
  const endpoints = [
    { name: 'Cloudflare', url: 'https://speed.cloudflare.com/__down?bytes=1' },
    { name: 'Fastly (US)', url: 'https://www.fastly.com' },
    { name: 'Akamai', url: 'https://www.akamai.com' }
  ];

  const results: GeoTest[] = [];

  for (const endpoint of endpoints) {
    const samples = [];
    for (let i = 0; i < 3; i++) {
      const start = performance.now();
      try {
        await fetch(endpoint.url, {
          method: 'HEAD',
          cache: 'no-store',
          mode: 'no-cors',
          signal: AbortSignal.timeout(5000) // 5s timeout
        });
        samples.push(performance.now() - start);
      } catch (e) {
        samples.push(-1);
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const validSamples = samples.filter(s => s > 0);
    const avgLatency = validSamples.length > 0
      ? validSamples.reduce((a, b) => a + b, 0) / validSamples.length
      : -1;

    results.push({
      endpoint: endpoint.name,
      location: 'Unknown', // Would need GeoIP API for actual location
      latency: avgLatency
    });
  }

  return results;
}

// Historical Tracking
export interface HistoricalTest {
  id: string;
  timestamp: number;
  target: string;
  latency?: number;
  packetLoss?: number;
  bandwidth?: number;
}

const STORAGE_KEY = 'connectivity-test-history';
const MAX_HISTORY = 100;

export function saveTestResult(result: HistoricalTest): void {
  try {
    const history = getTestHistory();
    history.unshift(result);

    // Keep only last MAX_HISTORY items
    const trimmed = history.slice(0, MAX_HISTORY);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.error('Failed to save test result:', e);
  }
}

export function getTestHistory(): HistoricalTest[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
}

export function clearTestHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear history:', e);
  }
}

export function getHistoryForTarget(target: string, limit: number = 20): HistoricalTest[] {
  const history = getTestHistory();
  return history
    .filter(test => test.target === target)
    .slice(0, limit);
}
